import React, { useState } from 'react';
import { AlertCircle, Loader2, ExternalLink, Search, ArrowRight } from 'lucide-react';
import axios from 'axios';

interface AddCustomStoreProps {
  onAdd: (store: {
    store_id: string;
    store_name: string;
    search_url: string;
    logo_url: string | null;
    country: string;
  }) => Promise<void>;
  onCancel: () => void;
}

export function AddCustomStore({ onAdd, onCancel }: AddCustomStoreProps) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationResult, setValidationResult] = useState<{
    isValid: boolean;
    searchUrl: string;
    reason: string;
    logo: string | null;
  } | null>(null);

  const handleValidate = async () => {
    setLoading(true);
    setError(null);
    setValidationResult(null);

    try {
      const response = await axios.post('http://localhost:3001/api/validate-store', {
        name,
        url
      });

      setValidationResult(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to validate store');
    } finally {
      setLoading(false);
    }
  };

  const handleTestSearch = () => {
    if (!validationResult?.searchUrl) return;
    window.open(`${validationResult.searchUrl}milk`, '_blank');
  };

  const handleAdd = async () => {
    if (!validationResult?.isValid || !validationResult.searchUrl) return;

    try {
      await onAdd({
        store_id: `custom-${name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`,
        store_name: name,
        search_url: validationResult.searchUrl,
        logo_url: validationResult.logo,
        country: 'CUSTOM'
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add store');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Add Custom Store</h3>

        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
            <Search size={18} />
            How to Find the Store's Search URL
          </h4>
          <ol className="text-sm text-blue-800 space-y-2">
            <li className="flex items-start gap-2">
              <span className="font-medium">1.</span>
              <span>Visit your store's website</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-medium">2.</span>
              <span>Search for any product (e.g., "milk" or "bread")</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-medium">3.</span>
              <span>After the search results appear, copy the URL from your browser's address bar</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-medium">4.</span>
              <span>Paste the complete search results URL below</span>
            </li>
          </ol>
          <div className="mt-3 text-sm text-blue-700">
            <strong>Example:</strong>
            <div className="mt-1 font-mono bg-white/50 p-2 rounded flex items-center gap-2 text-xs">
              <span className="opacity-50">https://www.example.com/search?q=milk</span>
              <ArrowRight size={14} className="text-blue-600" />
              <span>https://www.example.com/search?q=</span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Store Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Local Grocery Store"
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Store Website URL
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.example.com/search?q=milk"
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {validationResult && (
            <div className={`p-4 rounded-lg border ${
              validationResult.isValid
                ? 'bg-green-50 border-green-200'
                : 'bg-yellow-50 border-yellow-200'
            }`}>
              <div className="font-medium mb-2">
                {validationResult.isValid ? 'Store Validated!' : 'Validation Failed'}
              </div>
              <p className="text-sm mb-4">{validationResult.reason}</p>
              
              {validationResult.isValid && (
                <div className="space-y-4">
                  <div>
                    <div className="text-sm font-medium mb-1">Search URL Format:</div>
                    <code className="block p-2 bg-white rounded border text-sm font-mono">
                      {validationResult.searchUrl}
                    </code>
                  </div>
                  
                  <button
                    onClick={handleTestSearch}
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <ExternalLink size={16} />
                    Test search with "milk"
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            
            {!validationResult?.isValid ? (
              <button
                onClick={handleValidate}
                disabled={loading || !name || !url}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                Validate Store
              </button>
            ) : (
              <button
                onClick={handleAdd}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Add Store
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}